import {AlertTypes, apiClient, GenericFormContainer, IAlertData, Leap4Breadcrumbs, Mode, setAlert, Spinner, Mui, MuiIcons} from "@leap4/frontend";
import {useEffect, useMemo, useState} from "react";
import {connect, ConnectedProps} from "react-redux";
import {useHistory, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import {initialIntegrationObj, IIntegration} from "../base/integration.interface";
import {integrationBaseUrl} from "../../../../config/generals";
import {urlService} from "../../../routing/urlsService";
import {createNewObject, updateObjectById} from "../../../../actions/creatingObjectsFunc";
import {IntegrationContext} from "../base/integrationContext";
import IntegrationForm from "../integrationForm";
import { deleteItem } from "../../../../actions/deleteObjects";

const { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } = Mui;
const { Create: CreateIcon } = MuiIcons;

export interface IEditIntegrationProps extends PropsFromRedux {
    setIntegration: (data: IAlertData) => void;
}

const mapStateToProps = () => ({});

const mapActions = {
    setAlert
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

const _manageIntegration = (props: IEditIntegrationProps) => {
    console.log("manageIntegration rendering");
    const params = useParams<{ id?: string }>();
    const history = useHistory();
    const [integration, setIntegration] = useState<IIntegration>(initialIntegrationObj);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadError, setIsLoadError] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [demandWLTemplates, setDemandWLTemplates] = useState<Record<string, string>>({});

    const editIntegrationDataContextValue = useMemo(() => ({
        integration,
        setIntegration,
        demandWLTemplates,
        setDemandWLTemplates,
    }), [integration, setIntegration, demandWLTemplates]);

    const fetchIntegrationById = async () => {
        if (!params.id) {
            return;
        }
        const integrationData = await getIntegrationWithTemplatesById(params.id) as IIntegration & { demandWLTemplates: Record<string, string> };
        console.log("integrationData", integrationData);
        setIntegration(integrationData);
        setDemandWLTemplates(integrationData.demandWLTemplates || {});
    }

    const getIntegrationWithTemplatesById = async (id: string) => {
        const response = await apiClient.get(`${integrationBaseUrl}/${id}/withTemplates`,
            {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
            }
        )
        return response.data;
    }

    useQuery('fetchIntegrationById', fetchIntegrationById, {
        refetchOnWindowFocus: false,
        onError: (err: any) => {
            console.log("there was an error on getting integration data");
        }
    });

    const onSubmit = async () => {
        try {
            setIsLoading(true);
            const integrationPayload = {
                name: integration.name,
                adserver: integration.adserver,
                username: integration.username,
                password: integration.password,
                host: integration.host
            };
            if (params.id) {
                await updateObjectById(params.id, integrationPayload, integrationBaseUrl);
            } else {
                await createNewObject(integrationPayload, integrationBaseUrl);
            }
            setAlert({msg: "Integration saved successfully", type: AlertTypes.SUCCESS});
            history.push(urlService.integrations());
        } catch (err) {
            console.log("Error on saving integration", err);
            setAlert({msg: err.toString(), type: AlertTypes.FAILURE});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = () => {
        if (!params.id) return;
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        try {
            setIsLoading(true);
            await deleteItem(integrationBaseUrl, params.id);
            props.setAlert({ msg: "Integration deleted successfully", type: AlertTypes.SUCCESS });
            history.push(urlService.integrations());
        } catch (err) {
            console.error("Error deleting integration", err);
            props.setAlert({ msg: err.toString(), type: AlertTypes.FAILURE });
        } finally {
            setIsLoading(false);
            setIsDeleteModalOpen(false);
        }
    };

    useEffect(() => {
        if (!params.id) {
            return;
        }

    }, [params.id])

    if (isLoadError) {
        return (<>Error Loading the integration</>);
    }
    if (isLoading) {
        return (<Spinner/>);
    }

    return (
        <>
            <Leap4Breadcrumbs breadcrumbs={[{
                text: "Integration management dashboard",
                link: urlService.integrations(),
                icon: <CreateIcon/>
            },
                {
                    text: "Integration settings",
                    icon: <CreateIcon/>
                }
            ]}
            />
            <GenericFormContainer 
                mode={params.id ? Mode.Edit : Mode.Create} 
                onCreateOrEditFunc={onSubmit}
                onDeleteFunction={handleDelete}
                buttonsProps={{
                    delete: {
                        disabled: !params.id 
                    }
                }}
            >
                <IntegrationContext.Provider value={editIntegrationDataContextValue}>
                    <IntegrationForm mode={params.id ? "edit" : "create"}/>
                </IntegrationContext.Provider>
            </GenericFormContainer>

            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this integration?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} variant="contained" color="secondary">Delete</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export const ManageIntegration = connect(mapStateToProps, {
    setAlert
})(_manageIntegration);
